import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";

export default function NotificationBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);

  const loadCount = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  const loadItems = useCallback(() => {
    api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const iv = setInterval(loadCount, 60000);
    return () => clearInterval(iv);
  }, [loadCount]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadItems();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setCount(0);
    loadItems();
  };

  const clickItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); loadCount(); }
    setOpen(false);
    if (n.listing_id) navigate(`/dashboard`);
  };

  const renew = async (e, n) => {
    e.stopPropagation();
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); }
    setOpen(false);
    try { await api.post(`/listings/${n.listing_id}/renew`); } catch (err) {}
    navigate(`/pay/${n.listing_id}`);
  };

  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={toggle} className="relative w-9 h-9 flex items-center justify-center text-white/80 hover:text-gold transition-colors">
        <Bell size={20} />
        {count > 0 && (
          <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-gold text-black text-[10px] font-bold rounded-full flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-[#121212] border border-white/10 z-20" data-testid="notif-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="text-white font-medium text-sm">{t("notifications")}</span>
              {items.some((i) => !i.read) && (
                <button onClick={markAll} className="text-xs text-gold flex items-center gap-1 hover:opacity-80"><CheckCheck size={13} /> {t("mark_all_read")}</button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-10">{t("no_notifications")}</p>
              ) : (
                items.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`notif-item-${n.id}`}
                    onClick={() => clickItem(n)}
                    className={`block w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${n.read ? "" : "bg-gold/5"}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="w-2 h-2 rounded-full bg-gold mt-1.5 shrink-0" />}
                      <div className={n.read ? "pl-4" : ""}>
                        <p className="text-white text-sm font-medium">{n.title}</p>
                        <p className="text-white/50 text-xs mt-0.5 leading-relaxed">{n.message}</p>
                        {(n.type === "expiry_reminder" || n.type === "expired") && n.listing_id && (
                          <button
                            data-testid={`notif-renew-${n.id}`}
                            onClick={(e) => renew(e, n)}
                            className="mt-2 inline-flex items-center gap-1.5 bg-gold text-black text-xs font-medium px-3 py-1.5 hover:bg-gold-light transition-colors"
                          >
                            <RefreshCw size={13} /> {t("renew")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
